import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Box, Flex, Text, Button, Card } from "@radix-ui/themes";
import { MapPin, Loader2, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { IAny } from "@/types/breeze/Any";
import { useAuth } from "@/providers/AuthProvider";
import { useTenant } from "@/hooks/useCommonQueries";
import useMapStore from "./mapStore";
import CardCarousel from "@/components/carousel/CardCarousel";
import Map, { MapRef } from "@/components/map-view/Map";
import { renderCard } from "@/components/kanban/Card";

// Field selection interface
export interface FieldConfig {
  key: string;
  title: string;
  isVisible?: boolean;
}

// For type compatibility with CardCarousel
interface MapCardItem {
  id: number | string;
  entityName: string;
  title: string;
  description?: string;
  rawData: {
    entityName: string;
    id: number | string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// Define the props for the MapView component
interface MapViewProps {
  entityName: string;
  data: IAny[];
  isLoading: boolean;
  availableFields: { key: string; title: string }[];
  onItemClick: (id: number) => void;
}

// Custom style for the selected card (using Tailwind classes as Card component uses Tailwind)
const selectedCardClasses = "ring-2 ring-blue-500 shadow-lg scale-105";

// Fields that should always be hidden from cards
const HIDDEN_FIELDS = [
  "geo",
  "tenant_id",
  "_customStyles",
  "_customClasses",
  "_directClickHandler",
  "location",
  "color",
  "EntityName",
  "onClick",
];

// Use React.memo to prevent unnecessary rerenders
const MapViewComponent: React.FC<MapViewProps> = React.memo(
  ({
    entityName,
    data,
    isLoading = false,
    onItemClick,
    availableFields = [],
  }) => {
    // State for active marker/card
    const [activeIndex, setActiveIndex] = useState(0);

    // Create ref for the map component
    const mapRef = useRef<MapRef>(null);

    const { currentTenant } = useAuth();
    const { data: tenant, isLoading: isTenantLoading } = useTenant(
      currentTenant?.id || 0,
    );
    const { markers, processEntityData } = useMapStore();

    // Process data when it changes
    useEffect(() => {
      if (data && data.length > 0) {
        processEntityData(data);
      }
    }, [data, processEntityData]);

    // Get the effective selected fields - if none selected, use all available (except hidden)
    const effectiveSelectedFields = useMemo(() => {
      return availableFields.map((field) => field.key);
    }, [availableFields]);

    // Handle direct card click (not just selection in carousel)
    const handleCardClick = useCallback(
      (card: Record<string, unknown>, index: number) => {
        // Find the corresponding marker by id
        const marker = markers.find((m) => String(m.id) === String(card.id));

        if (marker) {
          const markerIndex = markers.indexOf(marker);

          // Update the active index to select the card
          setActiveIndex(markerIndex !== -1 ? markerIndex : index);

          // Center the map on this marker
          if (mapRef.current) {
            mapRef.current.centerOnMarker(String(marker.id));
          }

          // Call the onItemClick handler if provided
          if (onItemClick && typeof card.id === "number") {
            onItemClick(card.id);
          }
        } else {
          setActiveIndex(index);
        }

        return false;
      },
      [markers, onItemClick],
    );

    // Convert markers to card format for display
    const kanbanCards = useMemo(
      () =>
        markers.map((marker) => {
          // Extract images if they exist and if images field is selected
          const imagesFieldKey = `${entityName}_images`;
          const shouldIncludeImages =
            effectiveSelectedFields.includes(imagesFieldKey);

          const images =
            shouldIncludeImages && Array.isArray(marker.data.images)
              ? marker.data.images.filter(
                  (img: Record<string, unknown>) =>
                    img &&
                    typeof img.file_type === "string" &&
                    img.file_type.startsWith("image/"),
                )
              : [];

          // Basic fields that should always be included
          const baseCard: MapCardItem = {
            id: marker.id,
            title: marker.title || "Untitled",
            description: marker.description || "",
            entityName: entityName,
            created_at:
              (marker.data.created_at as string) || new Date().toISOString(),
            updated_at:
              (marker.data.updated_at as string) || new Date().toISOString(),
            column_id: "",
            position: 0,
            locked: false,
            // Include images only if they're explicitly selected
            ...(shouldIncludeImages && images.length > 0 && { images }),
            // Add rawData structure required by CardCarousel
            rawData: {
              entityName: entityName,
              id: marker.id,
              // Include images in rawData only if they're explicitly selected
              ...(shouldIncludeImages && images.length > 0 && { images }),
            },
          };

          // Add data fields that are selected
          for (const [key, value] of Object.entries(marker.data)) {
            const fieldKey = `${entityName}_${key}`;
            // Include if: field is selected OR no fields are selected AND not in hidden fields
            if (
              (effectiveSelectedFields.includes(fieldKey) ||
                effectiveSelectedFields.length === 0) &&
              !HIDDEN_FIELDS.includes(key)
            ) {
              baseCard[key] = value;
            }
          }

          return baseCard;
        }),
      [markers, entityName, effectiveSelectedFields],
    );

    // Custom wrapper for renderCard to adapt to CardCarousel
    const renderMapCard = useCallback(
      (card: Record<string, unknown>, index: number, isActive: boolean) => {
        // Extract only the fields we want to keep from the original card
        const {
          id,
          title,
          description,
          entityName,
          images,
          created_at,
          updated_at,
          column_id,
          position,
          locked,
          rawData,
          ...otherFields
        } = card as {
          id?: unknown;
          title?: unknown;
          description?: unknown;
          entityName?: unknown;
          images?: unknown;
          created_at?: unknown;
          updated_at?: unknown;
          column_id?: unknown;
          position?: unknown;
          locked?: unknown;
          rawData?: unknown;
          [key: string]: unknown;
        };

        // Create a clean card with only the fields we want
        const cleanCard: Record<string, unknown> = {
          id,
          title,
          description,
          entityName,
          created_at,
          updated_at,
          column_id: column_id || "",
          position: position || 0,
          locked: locked || false,
        };

        // Add images if they exist
        if (images) {
          cleanCard.images = images;
        }

        // Add other fields except known problematic ones
        Object.entries(otherFields).forEach(([key, value]) => {
          if (
            !key.includes("customClasses") &&
            !key.includes("directClickHandler") &&
            !key.includes("EntityName") &&
            !key.includes("Color") &&
            key !== "location" &&
            key !== "onClick" &&
            key !== "_customStyles"
          ) {
            cleanCard[key] = value;
          }
        });

        // Create a clean rawData object if it exists
        let cleanRawData: Record<string, unknown> | undefined;
        if (rawData && typeof rawData === "object") {
          const rawDataObj = rawData as Record<string, unknown>;
          cleanRawData = {
            entityName: entityName || "",
            id,
          };

          // Copy properties except the ones we want to exclude
          Object.keys(rawDataObj).forEach((key) => {
            if (
              ![
                "customClasses",
                "CustomClasses",
                "directClickHandler",
                "DirectClickHandler",
                "EntityName",
                "Color",
                "color",
              ].includes(key)
            ) {
              cleanRawData![key] = rawDataObj[key];
            }
          });

          // Add images if they exist
          if (images) {
            cleanRawData.images = images;
          }
        }

        // Create the final styled card
        const styledCard: Record<string, unknown> = {
          ...cleanCard,
          ...(cleanRawData && { rawData: cleanRawData }),
          _customClasses: isActive ? selectedCardClasses : "",
          _directClickHandler: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            handleCardClick(card, index);
            return false;
          },
        };

        // Render using the KanbanBoard Card component
        return renderCard(styledCard, { dragging: false });
      },
      [handleCardClick],
    );

    // Check if Google Maps API key is configured
    const hasGoogleMapsKey =
      tenant?.google_maps_key && tenant.google_maps_key.trim() !== "";

    // Convert markers to Map component format
    const mapMarkers = useMemo(
      () =>
        markers.map((marker, index) => ({
          id: String(marker.id),
          position: { lat: marker.lat, lng: marker.lng },
          isActive: index === activeIndex,
          data: {
            id: marker.id,
            title: marker.title,
            description: marker.description || "",
            locked: false,
            created_at: marker.data.created_at || new Date().toISOString(),
            updated_at: marker.data.updated_at || new Date().toISOString(),
          },
        })),
      [markers, activeIndex],
    );

    // Handle marker click
    const handleMarkerClick = useCallback(
      (id: string) => {
        const index = markers.findIndex((m) => String(m.id) === id);
        if (index !== -1) {
          setActiveIndex(index);
        }
      },
      [markers],
    );

    // Handle marker centered
    const handleMarkerCentered = useCallback(
      (id: string) => {
        const index = markers.findIndex((m) => String(m.id) === id);
        if (index !== -1 && index !== activeIndex) {
          setActiveIndex(index);
        }
      },
      [markers, activeIndex],
    );

    // Handle carousel index change
    const handleActiveIndexChange = useCallback(
      (index: number) => {
        setActiveIndex(index);

        if (mapRef.current && markers[index]) {
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.centerOnMarker(String(markers[index].id));
            }
          }, 200);
        }
      },
      [markers],
    );

    const isMapLoading = isLoading || isTenantLoading;

    // Show loading state
    if (isMapLoading) {
      return (
        <Box
          style={{
            height: "600px",
            backgroundColor: "var(--gray-2)",
            borderRadius: "var(--radius-3)",
          }}
        >
          <Flex
            align="center"
            justify="center"
            style={{ width: "100%", height: "100%" }}
          >
            <Flex align="center" gap="2">
              <Loader2
                style={{
                  width: "1.25rem",
                  height: "1.25rem",
                  animation: "spin 1s linear infinite",
                  color: "var(--accent-9)",
                }}
              />
              <Text size="2">Loading map...</Text>
            </Flex>
          </Flex>
        </Box>
      );
    }

    // Show message if Google Maps key is not configured
    if (!hasGoogleMapsKey) {
      return (
        <Box
          style={{
            minHeight: "600px",
            backgroundColor: "var(--gray-2)",
            borderRadius: "var(--radius-3)",
          }}
        >
          <Flex
            direction="column"
            align="center"
            justify="center"
            style={{ width: "100%", height: "100%", padding: "2rem" }}
          >
            <Card style={{ maxWidth: "32rem", width: "100%" }}>
              <Box p="6">
                <Flex direction="column" align="center" gap="4">
                  <Settings size={48} style={{ color: "var(--amber-9)" }} />
                  <Text size="5" weight="bold" align="center">
                    Google Maps API Key Required
                  </Text>
                  <Text size="3" color="gray" align="center">
                    To display maps, please configure your Google Maps API key
                    in your organisation settings.
                  </Text>
                  <Button variant="solid" asChild>
                    <Link
                      to={`/org/${currentTenant?.id}/settings/organisation`}
                    >
                      Go to Organisation Settings
                    </Link>
                  </Button>
                </Flex>
              </Box>
            </Card>
          </Flex>
        </Box>
      );
    }

    // Show message if no data
    if (!isMapLoading && markers.length === 0) {
      return (
        <Box
          style={{
            minHeight: "600px",
            backgroundColor: "var(--gray-2)",
            borderRadius: "var(--radius-3)",
          }}
        >
          <Flex
            direction="column"
            align="center"
            justify="center"
            style={{ width: "100%", height: "100%", padding: "2rem" }}
          >
            <MapPin
              size={48}
              style={{ color: "var(--gray-9)", marginBottom: "1rem" }}
            />
            <Text size="5" weight="medium" style={{ marginBottom: "0.5rem" }}>
              No location data available
            </Text>
            <Text
              size="3"
              color="gray"
              align="center"
              style={{ maxWidth: "28rem" }}
            >
              No items with geo data found. Add location coordinates to display
              items on the map.
            </Text>
          </Flex>
        </Box>
      );
    }

    return (
      <Flex
        direction="column"
        style={{ width: "100%", height: "100%" }}
        gap="4"
      >
        {/* Map container - fixed at exactly 40% of viewport height */}
        <Box
          style={{
            width: "100%",
            height: "40vh",
            minHeight: "200px",
            borderRadius: "var(--radius-3)",
            overflow: "hidden",
            border: "1px solid var(--gray-6)",
          }}
          className="map-container"
        >
          <Map
            ref={mapRef}
            markers={mapMarkers}
            onMarkerClick={handleMarkerClick}
            onMarkerCentered={handleMarkerCentered}
          />
        </Box>

        {/* Map info */}
        <Flex justify="between" align="center">
          <Text size="1" color="gray">
            {markers.length} items displayed on map
          </Text>
          <Text size="1" color="gray">
            Click on a marker or card to view details
          </Text>
        </Flex>

        {/* Cards carousel - takes remaining space */}
        {markers.length > 0 && (
          <Box style={{ marginTop: "1rem", flexGrow: 1 }}>
            <CardCarousel
              items={kanbanCards}
              activeIndex={activeIndex}
              onActiveIndexChange={handleActiveIndexChange}
              renderItem={renderMapCard}
            />
          </Box>
        )}
      </Flex>
    );
  },
);

// Set display name for React DevTools
MapViewComponent.displayName = "MapView";

export default MapViewComponent;
